import React, { useState } from "react";
import { Loader2, Users, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { loadRazorpayScript } from "@/components/RazorpayCheckout";

export default function ModeUpgradeCard({ track, onRefresh }) {
  const [loading, setLoading] = useState(false);

  if (!track) return null;
  const pending = track.mode_upgrade?.status === "pending";
  const paid = track.mode_upgrade?.status === "paid";
  const available = !pending && !paid && track.team_upgrade_available;
  if (!pending && !paid && !available) return null;

  const amount = pending ? track.mode_upgrade.amount_inr : track.team_upgrade_amount_inr;

  const payBalance = async () => {
    const loaded = await loadRazorpayScript();
    if (!loaded) {
      toast.error("Failed to load payment gateway. Please check your connection.");
      return;
    }
    const { data: order } = await api.post("/payments/create-upgrade-order", { application_id: track.id });
    const options = {
      key: order.key_id,
      amount: order.amount,
      currency: order.currency,
      name: "Gulfudaan",
      description: `Team Recovery Upgrade — ${order.application_number}`,
      order_id: order.order_id,
      theme: { color: "var(--brand-primary)" },
      handler: async (response) => {
        try {
          await api.post("/payments/verify-upgrade", {
            application_id: track.id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          });
          toast.success("Balance paid! Our team will now recover your slip.");
          onRefresh && onRefresh();
        } catch (e) {
          toast.error("Payment verification failed. Please contact support.");
        }
      },
      modal: { ondismiss: () => setLoading(false) },
    };
    new window.Razorpay(options).open();
  };

  const handleClick = async () => {
    setLoading(true);
    try {
      if (available) {
        await api.post(`/applications/track/${track.application_number}/request-team-upgrade`);
      }
      await payBalance();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initiate payment");
      setLoading(false);
    }
  };

  if (paid) {
    return (
      <div data-testid="team-upgrade-paid-card" className="rounded-xl border border-green-300 bg-green-50 px-5 py-4 flex items-center gap-3">
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-green-100">
          <CheckCircle2 size={20} className="text-green-600" />
        </span>
        <div>
          <p className="font-heading font-bold text-green-800 text-base">Upgraded to Recovery by Our Team</p>
          <p className="text-xs text-[#54595f]">Balance payment received — our team is processing your slip recovery.</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="team-upgrade-card" className="rounded-xl border border-[#E9E4DB] bg-white shadow-sm p-5">
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[color-mix(in_oklab,var(--brand-primary,#b1976b),transparent_88%)]">
          <Users size={19} className="text-[var(--brand-primary,#b1976b)]" />
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-heading font-bold text-[#1A1A1A] text-base">
            {pending ? "Balance payment pending" : "Want our team to recover it for you?"}
          </p>
          <p className="text-xs text-[#54595f] mt-0.5">
            {pending
              ? "Complete the remaining payment to upgrade to Recovery by Our Team — our experts will recover your slip for you."
              : "Upgrade to Recovery by Our Team by paying only the price difference. Our experts handle everything."}
          </p>
        </div>
      </div>
      <button
        type="button"
        data-testid={pending ? "team-upgrade-pay-btn" : "team-upgrade-request-btn"}
        onClick={handleClick}
        disabled={loading}
        className="mt-4 w-full inline-flex items-center justify-center gap-2 h-11 rounded-md bg-[var(--brand-primary,#b1976b)] hover:bg-[#9E855A] text-white text-sm font-bold transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
        {pending ? `Pay Remaining ₹${amount}` : `Upgrade & Pay ₹${amount}`}
      </button>
    </div>
  );
}
